/**
 * Pure transforms turning a {@link HospitalHistoryResponse} into the shape the
 * history chart draws: one time series per triage level, resolved against the
 * canonical {@link triageMeta} palette, plus the x/y domains the scales need.
 *
 * Everything here is deterministic and side-effect-free (no clock, no DOM), so
 * the chart's data layer is fully unit-tested without rendering SVG.
 *
 * Gaps: a `null` queueLength is a real signal (collector downtime with nothing
 * to carry forward, see HistoryPoint) — it is preserved as `value: null` so the
 * chart can break the line rather than draw a misleading zero.
 */

import type { HospitalHistoryResponse, HospitalTrendResponse } from "@/api/generated/model";
import { triageMeta } from "@/lib/triage";

export interface ChartPoint {
  /** Bucket start as a Date (UTC instant). */
  t: Date;
  /** People waiting in the bucket (the bucket average for trends), or `null` for a gap. */
  value: number | null;
  /**
   * Lower edge of the spread band (bucket min) for the long-window trend, or
   * `null`/absent for the 5-minute history, which has no band. Paired with
   * {@link hi}: both present ⇒ the chart draws a translucent min/max band.
   */
  lo?: number | null;
  /** Upper edge of the spread band (bucket max); see {@link lo}. */
  hi?: number | null;
}

export interface ChartSeries {
  triageCode: number;
  /** Italian triage label (from the canonical palette). */
  label: string;
  /** Triage reference colour. */
  color: string;
  points: ChartPoint[];
}

/** A chart point is drawable only with a finite value at a valid instant. */
export function isDrawable(p: ChartPoint): p is ChartPoint & { value: number } {
  return p.value !== null && !Number.isNaN(p.t.getTime());
}

/**
 * A point contributes to the spread band only when both edges are present at a
 * valid instant. The 5-minute history has no band (lo/hi absent), so this is
 * always `false` there — the chart then draws lines only.
 */
export function isBandDrawable(p: ChartPoint): p is ChartPoint & { lo: number; hi: number } {
  return p.lo != null && p.hi != null && !Number.isNaN(p.t.getTime());
}

/**
 * Build one {@link ChartSeries} per triage level present in the response,
 * ordered most-urgent-first (by severity rank). Buckets are parsed to Date.
 */
export function buildChartSeries(response: HospitalHistoryResponse): ChartSeries[] {
  return [...response.series]
    .map((s) => {
      const meta = triageMeta(s.triageCode);
      return {
        triageCode: s.triageCode,
        label: meta.labelIt,
        color: meta.colorVar,
        severityRank: meta.severityRank,
        points: s.points.map<ChartPoint>((p) => ({
          t: new Date(p.bucket),
          value: p.queueLength,
        })),
      };
    })
    .sort((a, b) => a.severityRank - b.severityRank)
    .map(({ severityRank: _severityRank, ...series }) => series);
}

/**
 * Build one {@link ChartSeries} per triage level from a long-window
 * {@link HospitalTrendResponse}, ordered most-urgent-first. Each point carries
 * the bucket average as its `value` plus the min/max spread as `lo`/`hi`, so the
 * chart can draw an average line inside a band. Empty buckets (honest gaps) keep
 * `null` across all three, breaking the line rather than inventing a value.
 */
export function buildTrendSeries(response: HospitalTrendResponse): ChartSeries[] {
  return [...response.series]
    .map((s) => {
      const meta = triageMeta(s.triageCode);
      return {
        triageCode: s.triageCode,
        label: meta.labelIt,
        color: meta.colorVar,
        severityRank: meta.severityRank,
        points: s.points.map<ChartPoint>((p) => ({
          t: new Date(p.bucket),
          value: p.avg,
          lo: p.min,
          hi: p.max,
        })),
      };
    })
    .sort((a, b) => a.severityRank - b.severityRank)
    .map(({ severityRank: _severityRank, ...series }) => series);
}

/** `true` when at least one series has a drawable point. */
export function hasAnySamples(series: ChartSeries[]): boolean {
  return series.some((s) => s.points.some(isDrawable));
}

/**
 * The [start, end] time domain spanning every drawable point, or `null` when
 * there is nothing to plot.
 */
export function timeDomain(series: ChartSeries[]): [Date, Date] | null {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const s of series) {
    for (const p of s.points) {
      if (!isDrawable(p)) continue;
      const t = p.t.getTime();
      if (t < min) min = t;
      if (t > max) max = t;
    }
  }
  if (min === Number.POSITIVE_INFINITY) return null;
  return [new Date(min), new Date(max)];
}

export interface SeriesSummary {
  /** Most-recent drawable value in the window. */
  current: number;
  /** Smallest drawable value in the window. */
  min: number;
  /** Largest drawable value in the window. */
  max: number;
}

/**
 * Concise stats for one series — the data behind the chart's screen-reader
 * summary (a per-bucket table would be hundreds of rows). `null` when the
 * series has no drawable points.
 */
export function seriesSummary(series: ChartSeries): SeriesSummary | null {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let current: number | null = null;
  for (const p of series.points) {
    if (!isDrawable(p)) continue;
    // For a banded (trend) series the honest spread is the band edges, not the
    // averages; the 5-minute history has no band, so lo/hi fall back to value.
    const lo = p.lo ?? p.value;
    const hi = p.hi ?? p.value;
    if (lo < min) min = lo;
    if (hi > max) max = hi;
    current = p.value; // points are time-ordered, so the last wins
  }
  if (current === null) return null;
  return { current, min, max };
}

/**
 * The upper bound of the y-axis: the largest drawable value, floored at 1 so a
 * flat all-zero window still yields a sane, non-degenerate scale.
 *
 * When `hidden` is given, muted series are excluded so the axis rescales to the
 * *visible* peak (still anchored at 0 — honesty CC-H4): soloing a small line
 * lets it fill the chart instead of being flattened against a taller neighbour.
 * A series with no drawable points contributes nothing regardless.
 */
export function valueMax(series: ChartSeries[], hidden?: ReadonlySet<number>): number {
  let max = 0;
  for (const s of series) {
    if (hidden?.has(s.triageCode)) continue;
    for (const p of s.points) {
      if (!isDrawable(p)) continue;
      // The band's upper edge (bucket max) can exceed the average, so the scale
      // must clear `hi` when a band is present; history points have no hi.
      const top = p.hi ?? p.value;
      if (top > max) max = top;
    }
  }
  return Math.max(1, max);
}

/** `true` when the series has at least one drawable point (so it can be muted). */
export function hasSamples(series: ChartSeries): boolean {
  return series.points.some(isDrawable);
}

/**
 * Toggle one triage series' visibility in the legend, returning a NEW hidden-set
 * (never mutates the input, so it drops straight into React state).
 *
 * Guards, in order:
 *  - a series with no drawable points can't be toggled (its legend button is
 *    disabled) — return unchanged;
 *  - un-muting an already-hidden series always works;
 *  - muting is refused when it would hide the *last* series that still has data,
 *    so the chart never collapses to an empty, axis-only frame (A3-5). The
 *    button stays pressed in that case.
 */
export function toggleSeriesVisibility(
  series: ChartSeries[],
  hidden: ReadonlySet<number>,
  code: number,
): Set<number> {
  const next = new Set(hidden);
  const target = series.find((s) => s.triageCode === code);
  if (target === undefined || !hasSamples(target)) return next;

  if (next.has(code)) {
    next.delete(code);
    return next;
  }

  // About to mute: refuse if it would leave nothing visible with data.
  const visibleWithData = series.filter((s) => hasSamples(s) && !next.has(s.triageCode));
  if (visibleWithData.length <= 1) return next;

  next.add(code);
  return next;
}

/**
 * Look-back windows served by the 5-minute `/history` endpoint (≤48h). Kept as a
 * union so the generated hook's `hours` param stays type-checked.
 */
export type WindowHours = 24 | 48;

/** Long-window tokens served by the hourly-aggregate `/trend` endpoint. */
export type TrendWindow = "7d" | "30d" | "90d" | "all";

/**
 * One step on the detail screen's unified time-zoom. Short steps read the
 * 5-minute history (fine detail, gaps carried forward); long steps read the
 * long-window trend (hourly/daily buckets, honest gaps, min/max band). The
 * selector presents them as one continuous zoom from "24h" out to "Tutto".
 */
export type TimeZoom =
  | {
      readonly id: string;
      readonly label: string;
      readonly summary: string;
      readonly kind: "history";
      readonly hours: WindowHours;
    }
  | {
      readonly id: string;
      readonly label: string;
      readonly summary: string;
      readonly kind: "trend";
      readonly window: TrendWindow;
    };

/** The zoom steps, shortest → longest, as rendered in the selector. */
export const ZOOM_OPTIONS: readonly TimeZoom[] = [
  { id: "24h", label: "24h", summary: "nelle ultime 24 ore", kind: "history", hours: 24 },
  { id: "48h", label: "48h", summary: "nelle ultime 48 ore", kind: "history", hours: 48 },
  { id: "7d", label: "7g", summary: "negli ultimi 7 giorni", kind: "trend", window: "7d" },
  { id: "30d", label: "30g", summary: "negli ultimi 30 giorni", kind: "trend", window: "30d" },
  { id: "90d", label: "90g", summary: "negli ultimi 90 giorni", kind: "trend", window: "90d" },
  { id: "all", label: "Tutto", summary: "in tutto lo storico", kind: "trend", window: "all" },
] as const;

/** The default zoom on first load — the recent day, matching the prior default. */
export const DEFAULT_ZOOM: TimeZoom = ZOOM_OPTIONS[0]!;

// ── Deep-linkable URL state (issue #180) ──────────────────────────────────────

/**
 * Query-param key carrying the detail time-window on the URL, so a zoom is
 * shareable/bookmarkable (`/ps/bolzano?finestra=30g`). IT-facing by decision:
 * the token IS the on-screen selector label (24h/48h/7g/30g/90g/Tutto), not the
 * internal {@link TimeZoom.id}, so a shared link reads the same as the UI.
 */
export const ZOOM_PARAM = "finestra";

/**
 * RESERVED, not wired this PR: the scrubbed moment (issue #94). Its URL shape is
 * undefined, so the param is only claimed here for forward-compat — nothing
 * reads or writes it yet. Wire it when #94's instant gains a defined encoding.
 */
export const INSTANT_PARAM = "istante";

/**
 * Format a zoom as its URL token — the exact on-screen label. Round-trips with
 * {@link zoomFromToken} for every {@link ZOOM_OPTIONS} entry.
 */
export function zoomToToken(zoom: TimeZoom): string {
  return zoom.label;
}

/**
 * Parse a `?finestra=` token back to a {@link TimeZoom}, matching the on-screen
 * label case-insensitively. Any missing, empty, or unrecognised token falls back
 * to {@link DEFAULT_ZOOM} — a bad deep-link degrades to the default view, never a
 * crash or blank screen.
 */
export function zoomFromToken(token: string | null | undefined): TimeZoom {
  if (token == null) return DEFAULT_ZOOM;
  const wanted = token.trim().toLowerCase();
  return ZOOM_OPTIONS.find((z) => z.label.toLowerCase() === wanted) ?? DEFAULT_ZOOM;
}
